import type { HttpError } from "@/interfaces/http-client";

export enum HttpStatusCode {
  UNAUTHORIZED = 401,
  NOT_FOUND = 404,
  SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  UNAVAILABLE_SERVICE = 503,
}

type ErrorHandler = (error: HttpError) => void;
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
} as const;

class HttpClient {
  private errorHandler: ErrorHandler | null = null;

  setErrorHandler(handler: ErrorHandler): void {
    this.errorHandler = handler;
  }

  async request<T>(url: string, options?: RequestInit): Promise<T> {
    try {
      const response = await this.executeRequest(url, options);
      this.validateResponse(response);
      return await this.parseResponse<T>(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  private async executeRequest(url: string, options?: RequestInit): Promise<Response> {
    return fetch(url, {
      ...options,
      headers: {
        ...DEFAULT_HEADERS,
        ...options?.headers,
      },
    });
  }

  private validateResponse(response: Response): void {
    if (!response.ok) {
      const error = this.createHttpError(response);
      this.notifyErrorHandler(error);
      throw error;
    }
  }

  private createHttpError(response: Response): HttpError {
    const error = new Error(
      `HTTP Error: ${response.status} ${response.statusText}`
    ) as HttpError;
    error.statusCode = response.status;
    error.response = response;
    return error;
  }

  private async parseResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');
    
    return isJson 
      ? await response.json()
      : (await response.text()) as unknown as T;
  }

  private handleError(error: unknown): never {
    if (error instanceof Error) {
      const httpError = error as HttpError;
      
      if (!httpError.statusCode) {
        httpError.statusCode = 0;
        this.notifyErrorHandler(httpError);
      }
      
      throw httpError;
    }
    throw error;
  }

  private notifyErrorHandler(error: HttpError): void {
    this.errorHandler?.(error);
  }

  private createRequestOptions(
    method: HttpMethod,
    data?: unknown,
    options?: RequestInit
  ): RequestInit {
    return {
      ...options,
      method,
      body: data ? JSON.stringify(data) : undefined,
    };
  }

  async get<T>(url: string, options?: RequestInit): Promise<T> {
    return this.request<T>(url, this.createRequestOptions('GET', undefined, options));
  }

  async post<T>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(url, this.createRequestOptions('POST', data, options));
  }

  async put<T>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(url, this.createRequestOptions('PUT', data, options));
  }

  async delete<T>(url: string, options?: RequestInit): Promise<T> {
    return this.request<T>(url, this.createRequestOptions('DELETE', undefined, options));
  }

  async patch<T>(url: string, data?: unknown, options?: RequestInit): Promise<T> {
    return this.request<T>(url, this.createRequestOptions('PATCH', data, options));
  }
}

export const httpClient = new HttpClient();
